import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';

const StyledFailedToLoadFolderIllustration = styled.svg`
  --color-gray-50: ${({ theme }) => theme.color.gray[50].value};
  --color-gray-100: ${({ theme }) => theme.color.gray[100].value};
  --color-gray-200: ${({ theme }) => theme.color.gray[200].value};
  --color-gray-300: ${({ theme }) => theme.color.gray[300].value};
  --color-gray-400: ${({ theme }) => theme.color.gray[400].value};
  --color-gray-500: ${({ theme }) => theme.color.gray[500].value};
  --color-gray-600: ${({ theme }) => theme.color.gray[600].value};
  --color-gray-700: ${({ theme }) => theme.color.gray[700].value};
  --color-gray-800: ${({ theme }) => theme.color.gray[800].value};
  --color-gray-900: ${({ theme }) => theme.color.gray[900].value};

  .grass-1,
  .grass-2,
  .lines,
  .tree {
    fill: var(--color-gray-100);
  }

  .help,
  .folder-front {
    fill: var(--color-gray-200);
  }

  .folder-face,
  .error {
    fill: var(--color-gray-400);
  }
`;

const FailedToLoadFolderIllustration = ({
  className = undefined,
  dataTestId = undefined,
  size = 400,
  svgTitle = 'Failed to Load Folder',
  ...other
}) => {
  return (
    <StyledFailedToLoadFolderIllustration
      className={className}
      data-testid={dataTestId ? `${dataTestId}-failed-to-load-folder-svg` : 'failed-to-load-folder-svg'}
      fill="none"
      height={size}
      viewBox="0 0 400 400"
      width={size}
      xmlns="http://www.w3.org/2000/svg"
      {...other}
    >
      {svgTitle && <title>{svgTitle}</title>}
      <g className="illustration-access-failed-to-load">
        <g className="ground">
          <path
            className="ellipse-20"
            opacity="0.3"
            d="M368 352C368 352 284 352 200 352C116 352 32 352 32 352C32 352 72.32 272 200 272C325.16 272 368 352 368 352Z"
            fill="url(#ground_gradient_fill_rule)"
          />
          <path
            className="grass-2"
            fillRule="evenodd"
            clipRule="evenodd"
            d="M311.988 295.155C312.074 294.88 312.147 294.606 312.206 294.332C313.143 289.965 310.983 283.863 310.27 283.196C309.31 283.463 305.026 287.029 303.787 291.697C302.85 296.064 304.654 301.832 305.723 302.833C305.723 302.833 305.723 302.833 305.724 302.833C305.707 302.942 305.702 303.039 305.708 303.123C307.186 304.084 313.649 304.147 317.381 302.222C321.453 300.121 324.354 294.811 324.288 293.904C323.313 293.414 317.159 292.722 312.615 294.805C312.403 294.914 312.194 295.031 311.988 295.155Z"
          />
          <path
            className="tree"
            fillRule="evenodd"
            clipRule="evenodd"
            d="M88.8749 292.925C88.7718 294.302 88.6745 295.646 88.5798 296.954C87.6037 310.441 86.9117 320 83.1932 320C80.9484 320 80.1408 313.726 79.2941 300.953C77.8338 300.902 70.5831 299.934 68.8364 293.649C67.4023 288.488 67.6496 276.338 70.8624 276.338C72.9741 276.338 73.2814 278.945 73.6429 282.012C74.0884 285.791 74.6161 290.268 78.7047 291.424C78.6955 291.27 78.6863 291.116 78.677 290.961C78.677 288.178 78.6206 285.268 78.5644 282.365L78.5644 282.365C78.3075 269.102 78.0537 256 83.1932 256C87.304 256 88.4663 265.228 88.7796 275.952C92.6072 275.19 93.1673 270.85 93.6779 266.894C94.1411 263.304 94.5636 260.03 97.3484 260.03C101.121 260.03 100.239 272.24 98.5553 277.447C96.6205 283.429 90.8684 285.481 88.8891 285.784C88.8883 286.953 88.885 288.112 88.8818 289.253V289.253V289.254C88.8783 290.505 88.8749 291.732 88.8749 292.925Z"
          />
          <path
            className="grass-1"
            d="M72.2723 307.805C74.185 311.828 74.3596 318.537 73.5248 320C72.2723 320 67.6804 316.585 65.5932 312.195C63.5059 307.317 63.9234 300.976 64.3409 300C65.1757 300 70.185 303.415 72.2723 307.805Z"
          />
        </g>
        <g className="secondary-item">
          <path
            className="help"
            fillRule="evenodd"
            clipRule="evenodd"
            d="M110.616 308.493C102.547 312.084 98.915 321.548 102.508 329.616C106.101 337.684 115.566 341.317 123.634 337.725C131.703 334.133 135.335 324.67 131.742 316.601C128.149 308.533 118.684 304.901 110.616 308.493ZM123.143 332.689L120.219 333.99L118.917 331.066L121.841 329.765L123.143 332.689ZM121.125 320.015L120.408 321.946C119.831 323.481 119.564 324.651 120.54 326.843L117.617 328.145L117.291 327.414C116.575 325.806 116.582 324.052 117.159 322.516L118.152 319.868C118.458 319.101 118.454 318.227 118.096 317.423C117.38 315.815 115.479 315.085 113.871 315.801C112.263 316.517 111.533 318.418 112.249 320.026L109.326 321.327C107.888 318.097 109.339 314.316 112.569 312.878C115.799 311.44 119.581 312.891 121.019 316.121C121.592 317.408 121.587 318.811 121.125 320.015Z"
          />
          <path
            className="cloud"
            fillRule="evenodd"
            clipRule="evenodd"
            d="M336.576 97.1525H336.615C340.696 97.1525 344.609 98.7775 347.494 101.67C350.379 104.563 352 108.486 352 112.576C352 116.667 350.379 120.59 347.494 123.482C344.609 126.375 340.696 128 336.615 128H290.901C288.419 128 285.961 127.51 283.668 126.558C281.375 125.605 279.291 124.21 277.536 122.45C273.991 118.896 272 114.076 272 109.051C272 104.025 273.991 99.2054 277.536 95.6518C280.876 92.3029 285.338 90.3369 290.037 90.1215C293.917 81.7938 302.252 76 312 76C324.319 76 334.48 85.2446 336.038 97.1849L336.576 97.1525ZM328.264 106.847V102C328.264 97.6756 326.55 93.5284 323.5 90.4706C320.45 87.4128 316.313 85.6949 312 85.6949C303.869 85.6949 297.199 91.6281 295.93 99.3591L295.672 100.931L294.178 100.387C293.15 100.012 292.037 99.7966 290.901 99.7966C288.453 99.7966 286.105 100.772 284.374 102.507C282.643 104.243 281.67 106.596 281.67 109.051C281.67 111.505 282.643 113.859 284.374 115.595C286.105 117.33 288.453 118.305 290.901 118.305H336.615C338.131 118.305 339.584 117.702 340.656 116.627C341.728 115.553 342.33 114.096 342.33 112.576C342.33 111.057 341.728 109.6 340.656 108.525C339.584 107.451 338.131 106.847 336.615 106.847H328.264Z"
            fill="var(--color-gray-50)"
          />
        </g>
        <g className="folder-primary-item">
          <path
            className="lines"
            fillRule="evenodd"
            clipRule="evenodd"
            d="M58.2152 193.379C55.5583 192.618 54.0222 189.846 54.784 187.189C55.5459 184.533 58.3174 182.996 60.9742 183.758L83.6655 190.264C86.3224 191.026 87.8585 193.797 87.0966 196.454C86.3348 199.111 83.5633 200.647 80.9065 199.885L58.2152 193.379ZM82.038 141.46C80.667 139.06 81.5012 136.003 83.9014 134.632C86.3015 133.261 89.3586 134.096 90.7297 136.496L102.438 156.992C103.809 159.392 102.975 162.449 100.575 163.82C98.1748 165.191 95.1177 164.357 93.7466 161.956L82.038 141.46ZM145.747 130.295C143.077 129.58 140.332 131.164 139.617 133.834L133.507 156.635C132.792 159.305 134.376 162.049 137.046 162.764C139.716 163.48 142.46 161.895 143.176 159.225L149.286 136.425C150.001 133.755 148.417 131.011 145.747 130.295Z"
          />
          <g className="entire-folder">
            <path
              className="folder-back"
              d="M167.154 189.279L149.767 279.138L147.456 291.568L265.303 290.96C270.591 290.932 275.278 286.02 276.98 278.72L297.611 194.169C298.903 188.628 296.068 182.864 292.054 182.868L172.78 183C170.146 183.003 167.843 185.573 167.154 189.279Z"
              fill="var(--color-gray-300)"
            />
            <path
              className="folder-front"
              d="M253.998 272.706L241.521 167.512C241.01 163.202 237.728 159.987 233.853 160L206.493 160.094C204.468 160.101 202.527 161.001 201.088 162.601L187.769 175.915H111.676C107.026 175.94 103.453 180.544 104.069 185.716L115.981 284.493C116.495 288.797 119.77 292.008 123.639 292L256.254 291.726C271.024 292.046 272.434 287.482 272.434 287.482C255.507 291.407 254 272.718 253.998 272.706Z"
            />
            <path
              className="folder-face"
              d="M192.807 222.833C196.402 222.833 199.547 226.131 199.547 229.899C199.547 233.668 196.402 236.966 192.807 236.966C189.213 236.966 186.068 233.668 186.068 229.899C186.068 226.131 189.213 222.833 192.807 222.833ZM168.096 229.899C168.096 233.668 164.951 236.966 161.357 236.966C157.762 236.966 154.617 233.668 154.617 229.899C154.617 226.131 157.762 222.833 161.357 222.833C164.951 222.833 168.096 226.131 168.096 229.899ZM177.082 251.098C184.945 251.098 191.864 254.49 195.907 259.625L189.527 266.314C187.506 262.875 182.698 260.52 177.082 260.52C171.466 260.52 166.658 262.875 164.637 266.314L158.257 259.625C162.3 254.49 169.219 251.098 177.082 251.098Z"
            />
            <path
              className="error"
              fillRule="evenodd"
              clipRule="evenodd"
              d="M290.469 213.048C280.791 210.814 271.121 216.857 268.885 226.538C266.648 236.219 272.687 245.889 282.365 248.124C292.043 250.358 301.712 244.315 303.949 234.634C306.186 224.953 300.147 215.283 290.469 213.048ZM286.144 239.759L282.638 238.949L283.448 235.442L286.955 236.252L286.144 239.759ZM287.765 232.745L284.258 231.935L286.689 221.413L290.196 222.223L287.765 232.745Z"
            />
          </g>
        </g>
      </g>
      <defs>
        <linearGradient
          className="ground-gradient"
          id="ground_gradient_fill_rule"
          x1="200"
          y1="272"
          x2="200"
          y2="352"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor="var(--color-gray-300)" />
          <stop offset="0.390625" stopColor="var(--color-gray-200)" />
          <stop offset="1" stopColor="var(--color-gray-200)" stopOpacity="0" />
        </linearGradient>
      </defs>
    </StyledFailedToLoadFolderIllustration>
  );
};

FailedToLoadFolderIllustration.propTypes = {
  /** Adds additional class to the component */
  className: PropTypes.string,
  /** Id value used for testing */
  dataTestId: PropTypes.string,
  /** Adjusts the size of the svg based upon the width. The aspect ratio is locked. */
  size: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  /** Adds SVG accessibility title element  */
  svgTitle: PropTypes.string,
};

export { FailedToLoadFolderIllustration };
